import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Landmark, Plus, Trash2, Lock, BarChart3 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { rupiah, fmtDate } from "@/lib/format";
import { Card, Loading, EmptyState, PageHeader, StatCard, Button, Modal, Badge } from "@/components/common";

const METHODS = [["transfer", "Transfer"], ["cash", "Tunai"], ["edc", "EDC/Kartu"], ["other", "Lainnya"]];

export default function Accounting() {
  const { branch, can, isOwner } = useAuth();
  const [data, setData] = useState(null);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const canCreate = can("accounting_expenses", "create") || isOwner;
  const canDelete = can("accounting_expenses", "delete") || isOwner;

  const load = async () => {
    setLoading(true);
    try {
      const [d, c] = await Promise.all([
        api.get("/accounting-expenses", branch ? { branch_id: branch.id } : {}),
        cats.length ? Promise.resolve(cats) : api.get("/accounting-expenses/categories").catch(() => []),
      ]);
      setData(d);
      if (!cats.length) setCats(c);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [branch]);

  const del = async (e) => {
    if (!window.confirm(`Hapus pengeluaran "${e.category_label}" ${rupiah(e.amount)}? Angka di Laporan Laba Rugi akan ikut menyesuaikan.`)) return;
    try { await api.del(`/accounting-expenses/${e.id}`); toast.success("Pengeluaran dihapus"); load(); }
    catch (err) { toast.error(err.message); }
  };

  if (loading) return <Loading />;

  return (
    <>
      <PageHeader title="Pengeluaran Akunting" subtitle={branch?.name} icon={Landmark}
        actions={<div className="flex items-center gap-2">
          <Link to="/accounting-report" className="inline-flex items-center gap-2 px-3 h-10 rounded-xl border border-slate-300 text-sm text-slate-700 hover:bg-slate-50" data-testid="acc-report-link"><BarChart3 size={16} /> Laporan</Link>
          {canCreate && <Button onClick={() => setAdding(true)} data-testid="add-acc-btn"><Plus size={16} /> Catat Pengeluaran</Button>}
        </div>} />

      <div className="flex items-center gap-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 mb-5">
        <Lock size={14} className="text-primary" /> Modul khusus Owner / role berizin. Terpisah dari Kas Harian & Petty Cash kasir, dan otomatis masuk ke Laporan Laba Rugi.
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Total Periode Ini" value={rupiah(data?.total || 0)} sub={`${data?.count || 0} transaksi`} tone="danger" icon={Landmark} testid="acc-total" />
        {(data?.by_category || []).slice(0, 3).map((c) => (
          <StatCard key={c.key} label={c.label} value={rupiah(c.amount)} />
        ))}
      </div>

      {!data?.items?.length ? (
        <EmptyState icon={Landmark} title="Belum ada pengeluaran akunting" message="Catat biaya gaji, sewa, pembelian aset, dsb." action={canCreate && <Button onClick={() => setAdding(true)}><Plus size={16} /> Catat Pengeluaran</Button>} />
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-slate-400 border-b border-slate-100 bg-slate-50/50">
              <th className="py-3 px-4 font-medium">Tanggal</th><th className="py-3 px-4 font-medium">Kategori</th>
              <th className="py-3 px-4 font-medium">Vendor / Catatan</th><th className="py-3 px-4 font-medium">Metode</th>
              <th className="py-3 px-4 font-medium text-right">Jumlah</th>{canDelete && <th className="py-3 px-4"></th>}
            </tr></thead>
            <tbody>
              {data.items.map((e) => (
                <tr key={e.id} className="border-b border-slate-50" data-testid={`acc-row-${e.id}`}>
                  <td className="py-3 px-4 text-slate-400 text-xs">{fmtDate(e.created_at)}</td>
                  <td className="py-3 px-4"><Badge className="bg-primary/10 text-primary">{e.category_label}</Badge></td>
                  <td className="py-3 px-4 text-slate-600">{e.vendor || e.note || "-"}</td>
                  <td className="py-3 px-4 text-slate-500">{METHODS.find((m) => m[0] === e.method)?.[1] || e.method}</td>
                  <td className="py-3 px-4 text-right font-semibold text-rose-600">{rupiah(e.amount)}</td>
                  {canDelete && <td className="py-3 px-4 text-right"><button onClick={() => del(e)} className="w-8 h-8 rounded-lg hover:bg-rose-50 flex items-center justify-center text-rose-500" data-testid={`delete-acc-${e.id}`}><Trash2 size={15} /></button></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {adding && <AccModal cats={cats} branch={branch} onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} />}
    </>
  );
}

function AccModal({ cats, branch, onClose, onSaved }) {
  const [form, setForm] = useState({ category: cats[0]?.key || "gaji", amount: "", vendor: "", method: "transfer", note: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    if (!branch) return toast.error("Pilih cabang dulu");
    if (!form.category || !Number(form.amount)) return toast.error("Lengkapi kategori & jumlah");
    setBusy(true);
    try {
      await api.post("/accounting-expenses", { branch_id: branch.id, category: form.category, amount: Number(form.amount), vendor: form.vendor, method: form.method, note: form.note });
      toast.success("Pengeluaran dicatat"); onSaved();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title="Catat Pengeluaran Akunting" maxWidth="max-w-md">
      <div className="space-y-3">
        <label className="block"><span className="text-sm font-medium text-slate-600 mb-1 block">Kategori</span>
          <select value={form.category} onChange={(e) => set("category", e.target.value)} className="w-full h-10 px-3 rounded-xl border border-slate-300" data-testid="acc-cat">{cats.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}</select></label>
        <label className="block"><span className="text-sm font-medium text-slate-600 mb-1 block">Jumlah</span><input type="number" value={form.amount} onChange={(e) => set("amount", e.target.value)} className="w-full h-10 px-3 rounded-xl border border-slate-300" data-testid="acc-amount" /></label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block"><span className="text-sm font-medium text-slate-600 mb-1 block">Vendor / Penerima</span><input value={form.vendor} onChange={(e) => set("vendor", e.target.value)} className="w-full h-10 px-3 rounded-xl border border-slate-300" /></label>
          <label className="block"><span className="text-sm font-medium text-slate-600 mb-1 block">Metode</span><select value={form.method} onChange={(e) => set("method", e.target.value)} className="w-full h-10 px-3 rounded-xl border border-slate-300">{METHODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label>
        </div>
        <label className="block"><span className="text-sm font-medium text-slate-600 mb-1 block">Catatan</span><textarea value={form.note} onChange={(e) => set("note", e.target.value)} rows={2} className="w-full px-3 py-2 rounded-xl border border-slate-300" /></label>
        <Button className="w-full" onClick={save} disabled={busy} data-testid="acc-save">{busy ? "Menyimpan..." : "Simpan"}</Button>
      </div>
    </Modal>
  );
}
